import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoadingState } from '@/components/LoadingState';
import { RequireAuth } from '@/components/RequireAuth';
import { RequireStudent } from '@/components/RequireStudent';
import { LoginPage } from '@/pages/LoginPage';
import { StudentLoginPage } from '@/pages/student/StudentLoginPage';
import { KioskPage } from '@/pages/KioskPage';
import { KioskSetupPage } from '@/pages/KioskSetupPage';
import { NotFoundPage } from '@/pages/Placeholders';

// Lazy-loaded — kept out of the Kiosk-iPad initial bundle. Chunks load on demand
// when a teacher navigates to /dashboard or /admin.
const DashboardLayout = lazy(() =>
  import('@/pages/dashboard/DashboardLayout').then((m) => ({ default: m.DashboardLayout })),
);
const OverviewPage = lazy(() =>
  import('@/pages/dashboard/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
const RoomsPage = lazy(() =>
  import('@/pages/dashboard/RoomsPage').then((m) => ({ default: m.RoomsPage })),
);
const StudentsPage = lazy(() =>
  import('@/pages/dashboard/StudentsPage').then((m) => ({ default: m.StudentsPage })),
);
const StudentDetailPage = lazy(() =>
  import('@/pages/dashboard/StudentDetailPage').then((m) => ({ default: m.StudentDetailPage })),
);
const CheckinPage = lazy(() =>
  import('@/pages/dashboard/CheckinPage').then((m) => ({ default: m.CheckinPage })),
);
const BerichtePage = lazy(() =>
  import('@/pages/dashboard/BerichtePage').then((m) => ({ default: m.BerichtePage })),
);
const EinstellungenPage = lazy(() =>
  import('@/pages/dashboard/EinstellungenPage').then((m) => ({ default: m.EinstellungenPage })),
);
const AssessmentIndexPage = lazy(() =>
  import('@/pages/dashboard/AssessmentIndexPage').then((m) => ({ default: m.AssessmentIndexPage })),
);
const AssessmentFormPage = lazy(() =>
  import('@/pages/dashboard/AssessmentFormPage').then((m) => ({ default: m.AssessmentFormPage })),
);
const SozialpassPage = lazy(() =>
  import('@/pages/dashboard/SozialpassPage').then((m) => ({ default: m.SozialpassPage })),
);
const ClassesIndexPage = lazy(() =>
  import('@/pages/dashboard/ClassesIndexPage').then((m) => ({ default: m.ClassesIndexPage })),
);
const ClassOverviewPage = lazy(() =>
  import('@/pages/dashboard/ClassOverviewPage').then((m) => ({ default: m.ClassOverviewPage })),
);
const AdminLayout = lazy(() =>
  import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const AdminOverviewPage = lazy(() =>
  import('@/pages/admin/AdminOverviewPage').then((m) => ({ default: m.AdminOverviewPage })),
);
const AdminStudentsPage = lazy(() =>
  import('@/pages/admin/AdminStudentsPage').then((m) => ({ default: m.AdminStudentsPage })),
);
const AdminTeachersPage = lazy(() =>
  import('@/pages/admin/AdminTeachersPage').then((m) => ({ default: m.AdminTeachersPage })),
);
const AdminRoomsPage = lazy(() =>
  import('@/pages/admin/AdminRoomsPage').then((m) => ({ default: m.AdminRoomsPage })),
);
const AdminProjectDaysPage = lazy(() =>
  import('@/pages/admin/AdminProjectDaysPage').then((m) => ({ default: m.AdminProjectDaysPage })),
);
const AdminAuditLogPage = lazy(() =>
  import('@/pages/admin/AdminAuditLogPage').then((m) => ({ default: m.AdminAuditLogPage })),
);
const AdminDsgvoPage = lazy(() =>
  import('@/pages/admin/AdminDsgvoPage').then((m) => ({ default: m.AdminDsgvoPage })),
);
const AdminSettingsPage = lazy(() =>
  import('@/pages/admin/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })),
);
const OfferingsPage = lazy(() =>
  import('@/pages/admin/OfferingsPage').then((m) => ({ default: m.OfferingsPage })),
);
const AdminAssignmentsHubPage = lazy(() =>
  import('@/pages/admin/AdminAssignmentsHubPage').then((m) => ({
    default: m.AdminAssignmentsHubPage,
  })),
);
const AdminAssessmentsPage = lazy(() =>
  import('@/pages/admin/AdminAssessmentsPage').then((m) => ({ default: m.AdminAssessmentsPage })),
);
const AdminMaintenancePage = lazy(() =>
  import('@/pages/admin/AdminMaintenancePage').then((m) => ({ default: m.AdminMaintenancePage })),
);
const AdminPraxismodulHubPage = lazy(() =>
  import('@/pages/admin/AdminPraxismodulHubPage').then((m) => ({
    default: m.AdminPraxismodulHubPage,
  })),
);

// Schüler-Bereich (OU-1c/1d) — strictly separate from the teacher dashboard.
const StudentLayout = lazy(() =>
  import('@/pages/student/StudentLayout').then((m) => ({ default: m.StudentLayout })),
);
const StudentDashboard = lazy(() =>
  import('@/pages/student/StudentDashboard').then((m) => ({ default: m.StudentDashboard })),
);
const SelfReportsListPage = lazy(() =>
  import('@/pages/student/SelfReportsListPage').then((m) => ({ default: m.SelfReportsListPage })),
);
const SelfReportFormPage = lazy(() =>
  import('@/pages/student/SelfReportFormPage').then((m) => ({ default: m.SelfReportFormPage })),
);
const AttendancePage = lazy(() =>
  import('@/pages/student/AttendancePage').then((m) => ({ default: m.AttendancePage })),
);
const StudentSozialpassPage = lazy(() =>
  import('@/pages/student/SozialpassPage').then((m) => ({ default: m.SozialpassPage })),
);

export function App(): JSX.Element {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Suspense fallback={<LoadingState variant="spinner" label="Seite wird geladen …" />}>
        <Routes>
          <Route
            path="/"
            element={
              <ErrorBoundary autoReloadAfterMs={10_000}>
                <KioskPage />
              </ErrorBoundary>
            }
          />
          <Route path="/kiosk" element={<Navigate to="/" replace />} />
          <Route
            path="/kiosk/setup"
            element={
              <ErrorBoundary autoReloadAfterMs={10_000}>
                <KioskSetupPage />
              </ErrorBoundary>
            }
          />

          <Route path="/login" element={<LoginPage />} />

          {/* Schüler-Bereich (OU-1c/1d) — separate login + guard. */}
          <Route path="/student/login" element={<StudentLoginPage />} />
          <Route
            path="/student"
            element={
              <RequireStudent>
                <ErrorBoundary>
                  <StudentLayout />
                </ErrorBoundary>
              </RequireStudent>
            }
          >
            <Route index element={<StudentDashboard />} />
            <Route path="self-reports" element={<SelfReportsListPage />} />
            <Route path="self-reports/:pdId" element={<SelfReportFormPage />} />
            <Route path="attendance" element={<AttendancePage />} />
            <Route path="sozialpass" element={<StudentSozialpassPage />} />
          </Route>

          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <ErrorBoundary>
                  <DashboardLayout />
                </ErrorBoundary>
              </RequireAuth>
            }
          >
            <Route index element={<OverviewPage />} />
            <Route path="raeume" element={<RoomsPage />} />
            <Route path="schueler" element={<StudentsPage />} />
            <Route path="schueler/:id" element={<StudentDetailPage />} />
            <Route path="checkin" element={<CheckinPage />} />
            <Route path="bewertungen" element={<AssessmentIndexPage />} />
            <Route path="bewertungen/:pdId" element={<AssessmentFormPage />} />
            <Route path="sozialpass" element={<SozialpassPage />} />
            <Route path="klassen" element={<ClassesIndexPage />} />
            <Route path="klassen/:klasse" element={<ClassOverviewPage />} />
            <Route path="berichte" element={<BerichtePage />} />
            <Route path="einstellungen" element={<EinstellungenPage />} />
          </Route>

          <Route
            path="/admin"
            element={
              <RequireAuth>
                <ErrorBoundary>
                  <AdminLayout />
                </ErrorBoundary>
              </RequireAuth>
            }
          >
            <Route index element={<AdminOverviewPage />} />
            <Route path="students" element={<AdminStudentsPage />} />
            <Route path="teachers" element={<AdminTeachersPage />} />
            <Route path="rooms" element={<AdminRoomsPage />} />
            <Route path="offerings" element={<OfferingsPage />} />
            <Route
              path="assignments/import"
              element={<Navigate to="/admin/assignments?tab=import" replace />}
            />
            <Route path="assignments" element={<AdminAssignmentsHubPage />} />
            <Route path="assessments" element={<AdminAssessmentsPage />} />
            <Route path="praxismodul" element={<AdminPraxismodulHubPage />} />
            <Route
              path="praxismodul/import"
              element={<Navigate to="/admin/praxismodul?tab=import" replace />}
            />
            <Route path="project-days" element={<AdminProjectDaysPage />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
            <Route path="dsgvo" element={<AdminDsgvoPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route
              path="schoolyears"
              element={<Navigate to="/admin/settings?section=schuljahre" replace />}
            />
            <Route path="maintenance" element={<AdminMaintenancePage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
